<script setup lang="ts">
import { labels } from '@/appdata/labels'
import { useAuthStore } from '@/stores/auth'
import { handleError } from '@/appdata/globals'
import { useAppDataStore } from '@/stores/app'
import { useRoute } from 'vue-router'
import { onBeforeMount, onUpdated, ref } from 'vue'
import { Notification, NotificationGroup } from '@progress/kendo-vue-notification'
import { Fade } from '@progress/kendo-vue-animation';
import router from '@/router/index'
import * as bootstrap from 'bootstrap'
import LoadingIndicator from '@/components/LoadingIndicator.vue'

const location = useRoute()
const appData = useAppDataStore()
const authData = useAuthStore()

const trips = ref<HTMLElement|null>(null);
const suppliers = ref<HTMLElement|null>(null);
const packages = ref<HTMLElement|null>(null);
const services = ref<HTMLElement|null>(null);
const contacts = ref<HTMLElement|null>(null);
const media = ref<HTMLElement|null>(null);
const locations = ref<HTMLElement|null>(null);
const users = ref<HTMLElement|null>(null);
const settings = ref<HTMLElement|null>(null);

const navigateTo = (alias: string) => {
  router.push({ name: alias });
}

const bindTooltips = () => {
  console.info("App | bindTooltips")
  const tooltipTriggerList = document.querySelectorAll('#section-navigation [data-bs-toggle="tooltip"]')
  return [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl))
};

onBeforeMount(() => {
  console.info("App | onBeforeMount")
  authData.fetchData()
    .then(() => {
      return appData.initializeAppData();
    })
    .then(() => {
      if (authData.user?.enabled && (!appData.permissions?.trips?.read)) {
        router.push({ name: 'suppliers' })
      }
    })
    .catch((error) => { handleError(error); })
    .finally(() => {
      appData.setLoading(false);
    })
})

onUpdated(bindTooltips)

const onSuccessDismiss = (index: number) => {
  appData.notifications.success = appData.notifications.success.filter((item, idx) => {
    return idx !== index;
  })
}

const onErrorDismiss = (index: number) => {
  appData.notifications.errors = appData.notifications.errors.filter((item, idx) => {
    return idx !== index;
  })
}

const onWarningDismiss = (index: number) => {
  appData.notifications.warnings = appData.notifications.warnings.filter((item, idx) => {
    return idx !== index;
  })
}

const onNoticeDismiss = (index: number) => {
  appData.notifications.info = appData.notifications.info.filter((item, idx) => {
    return idx !== index;
  })
}

appData.$subscribe((mutation) => {
  console.debug("AppData | subscriber | ", mutation)
  const payload = 'payload' in mutation ? mutation.payload : null;
  if (payload?.notifications) {
    if (payload?.notifications?.errors) {
      setTimeout(() => {
        appData.notifications.errors.shift();
      }, 3000)
    }

    if (payload?.notifications?.success) {
      setTimeout(() => {
        appData.notifications.success.shift();
      }, 3000)
    }

    if (payload?.notifications?.warnings) {
      setTimeout(() => {
        appData.notifications.warnings.shift();
      }, 3000)
    }

    if (payload?.notifications?.info) {
      setTimeout(() => {
        appData.notifications.info.shift();
      }, 3000)
    }
  }
})
</script>

<template>
  <nav v-if="authData.userEnabled" id="title-bar" class="navbar navbar-expand-lg bg-primary text-white fixed-top" data-bs-theme="light">
    <div class="container-fluid">
      <div class="navbar-text navbar-brand">
        <div class="d-flex justify-content-center">
          <span class="brand-icon flex-grow-0">
            <img src="/favicon.webp" class="img-fluid" alt=""/>
          </span>
          <span class="d-inline-block ms-2 my-auto">{{ labels.branding.title }}</span>
        </div>
      </div>
    </div>
  </nav>

  <div v-if="authData.userEnabled" id="view-container" class="d-flex flex-fill">
    <div id="section-navigation" class="d-flex flex-column">
      <div v-if="appData.permissions.trips.read" data-bs-toggle="tooltip" :data-bs-title="labels.navigation.trips.tooltip" data-bs-placement="right">
        <button type="button" ref="trips" class="btn btn-link d-block" :class="location.name === 'planner' ? 'active' : ''" @click.stop="navigateTo('planner')">
          <span class="fa-duotone fa-fw fa-earth-europe"></span>
          <span class="visually-hidden">Trips</span>
        </button>
      </div>
      <div v-if="appData.permissions.suppliers.read" data-bs-toggle="tooltip" :data-bs-title="labels.navigation.suppliers.tooltip" data-bs-placement="right">
      <button type="button" ref="suppliers" class="btn btn-link d-block" :class="location.name === 'suppliers' ? 'active' : ''" @click.stop="navigateTo('suppliers')">
        <span class="fa-duotone fa-fw fa-car-bus"></span>
        <span class="visually-hidden">Suppliers</span>
      </button>
      </div>
      <div v-if="appData.permissions.services.read" data-bs-toggle="tooltip" :data-bs-title="labels.navigation.services.tooltip" data-bs-placement="right">
      <button type="button" ref="services" class="btn btn-link d-block" :class="location.name === 'services' ? 'active' : ''" @click.stop="navigateTo('services')">
        <span class="fa-duotone fa-fw fa-archway"></span>
        <span class="visually-hidden">Services</span>
      </button>
      </div>
      <div v-if="appData.permissions.packages.read" data-bs-toggle="tooltip" :data-bs-title="labels.navigation.packages.tooltip" data-bs-placement="right">
        <button type="button" ref="packages" class="btn btn-link d-block" :class="location.name === 'packages' ? 'active' : ''" @click.stop="navigateTo('packages')">
          <span class="fa-duotone fa-fw fa-map-location-dot"></span>
          <span class="visually-hidden">Packages</span>
        </button>
      </div>
      <div v-if="appData.permissions.contacts.read" data-bs-toggle="tooltip" :data-bs-title="labels.navigation.contacts.tooltip" data-bs-placement="right">
      <button type="button" ref="contacts" class="btn btn-link d-block" :class="location.name === 'contacts' ? 'active' : ''" @click.stop="navigateTo('contacts')">
        <span class="fa-duotone fa-fw fa-handshake"></span>
        <span class="visually-hidden">Contacts</span>
      </button>
      </div>
      <div v-if="appData.permissions.media.read" data-bs-toggle="tooltip" :data-bs-title="labels.navigation.mediaLibrary.tooltip" data-bs-placement="right">
      <button type="button" ref="media" class="btn btn-link d-block" :class="location.name === 'media' ? 'active' : ''" @click.stop="navigateTo('media')">
        <span class="fa-duotone fa-fw fa-image"></span>
        <span class="visually-hidden">Media</span>
      </button>
      </div>
      <div v-if="appData.permissions.settings.read" class="mt-auto" data-bs-toggle="tooltip" :data-bs-title="labels.navigation.locations.tooltip" data-bs-placement="right">
        <button type="button" ref="locations" class="btn btn-link d-block" :class="location.name === 'locations' ? 'active' : ''" @click.stop="navigateTo('locations')">
          <span class="fa-duotone fa-fw fa-location-dot"></span>
          <span class="visually-hidden">Locations</span>
        </button>
      </div>
      <div v-if="appData.permissions.users.read" data-bs-toggle="tooltip" :data-bs-title="labels.navigation.users.tooltip" data-bs-placement="right">
      <button type="button" ref="users" class="btn btn-link d-block" :class="location.name === 'users' ? 'active' : ''" @click.stop="navigateTo('users')">
        <span class="fa-duotone fa-fw fa-users"></span>
        <span class="visually-hidden">Users</span>
      </button>
      </div>
      <div v-if="appData.permissions.settings.read" data-bs-toggle="tooltip" :data-bs-title="labels.navigation.settings.tooltip" data-bs-placement="right">
      <button type="button" ref="settings" class="btn btn-link d-block" :class="location.name === 'settings' ? 'active' : ''" @click.stop="navigateTo('settings')">
        <span class="fa-duotone fa-fw fa-cog"></span>
        <span class="visually-hidden">Settings</span>
      </button>
      </div>
    </div>

    <RouterView />
    <LoadingIndicator :visible="appData.loading"/>

    <NotificationGroup
      :style="{
        top: '3rem',
        left: '50%',
        transform: 'translateX(-50%)',
        alignItems: 'flex-center',
      }"
    >
      <Fade :appear="appData.notifications.success.length > 0">
        <Notification
          v-for="(message, index) in appData.notifications.success"
          :key="index"
          :type="{
            style: 'success',
            icon: true,
          }"
          :closable="true"
          @close="onSuccessDismiss(index)"
        >
          <span v-html="message"></span>
        </Notification>
      </Fade>
      <Fade :appear="appData.notifications.info.length > 0">
        <Notification
          v-for="(message, index) in appData.notifications.info"
          :key="index"
          :type="{
            style: 'info',
            icon: true,
          }"
          :closable="true"
          @close="onNoticeDismiss(index)"
        >
          <span v-html="message"></span>
        </Notification>
      </Fade>
      <Fade :appear="appData.notifications.errors.length > 0">
        <Notification
          v-for="(message, index) in appData.notifications.errors"
          :key="index"
          :type="{
            style: 'error',
            icon: true,
          }"
          :closable="true"
          @close="onErrorDismiss(index)"
        >
          <span v-html="message"></span>
        </Notification>
      </Fade>
      <Fade :appear="appData.notifications.warnings.length > 0">
        <Notification
          v-for="(message, index) in appData.notifications.warnings"
          :key="index"
          :type="{
            style: 'warning',
            icon: true,
          }"
          :closable="true"
          @close="onWarningDismiss(index)"
        >
          <span v-html="message"></span>
        </Notification>
      </Fade>
    </NotificationGroup>
  </div>

  <div v-if="!authData.userEnabled" class="d-flex flex-fill flex-column justify-content-center vh-100">
    <div v-if="appData.loading && (!authData.authenticated)" class="my-auto mx-auto p-5 w-25 text-warning">
      <p class="text-center">
        <span class="fa-light fa-fw fa-5x fa-loader fa-spin"></span>
      </p>
      <p class="h5 text-center fw-bold text-uppercase">
        {{ labels.userLoggingIn }}
      </p>
    </div>
    <div v-else class="my-auto mx-auto p-5 w-25 text-warning">
      <p class="text-center">
        <span class="fa-light fa-fw fa-5x fa-exclamation-triangle"></span>
      </p>
      <p class="h5 text-center fw-bold text-uppercase">
        {{ labels.userDisabled }}
      </p>
    </div>
  </div>
</template>
